import { useCallback } from 'react';
import { matchRoutes, useLocation } from 'react-router';
import { routes } from '../Infrastructure/Routing/routes';

function useRouteMatch() {
	const currentLocation = useLocation();

	/**
	 * Method used to determine if the parameter 'path' passed in is the current location of the application.
	 * Returns true when the passed in 'path' was determined to be the current location, otherwise false.
	 */
	const isCurrentRoute = useCallback(
		(path: string) => {
			// routes from the react-router are prefixed with the / where the consumer may not require it
			if (!path.startsWith('/')) {
				path = `/${path}`;
			}

			const match = matchRoutes(routes, currentLocation);

			return (
				match &&
				match.length > 0 &&
				match.slice(-1)[0].pathname.localeCompare(path, 'en', {
					sensitivity: 'base'
				}) === 0
			);
		},
		[currentLocation]
	);

	const getCurrentRoute = useCallback(() => matchRoutes(routes, currentLocation), [currentLocation]);

	const replaceTerminalInRoute = useCallback(
		(newTerminalNumber: string) => {
			let newRoute = '';

			const matches = matchRoutes(routes, currentLocation);
			let index = 0;
			matches?.forEach((match) => {
				// For now we do not want to continue past the initial "resource" when switching terminals
				// Switching Terminals will always reroute to the Dispatch Board currently. Increase this value if we change that behavior.
				if (index > 1) {
					return;
				}

				if (match.route.path) {
					// If we find another non-terminalNumber url parameter, it should persist
					if (match.route.path.startsWith(':') && !match.route.path.includes('terminalNumber')) {
						const paramArray = match.params as Record<string, string>;
						const paramName = match.route.path.replace(':', '');
						const paramValue = paramArray[paramName];
						newRoute = `${newRoute}/${paramValue}`;
					} else {
						newRoute = `${newRoute}/${match.route.path}`;
					}
				}

				index = index + 1;
			});

			newRoute = newRoute.replace(':terminalNumber', newTerminalNumber);

			return newRoute;
		},
		[currentLocation]
	);

	return {
		isCurrentRoute,
		getCurrentRoute,
		replaceTerminalInRoute
	};
}

export default useRouteMatch;
